import React, { useState, useEffect } from "react";
import { EmotionChip } from "@/components/EmotionChip";
import axios from "axios";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import { Zap, BookOpen } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const CE_COLORS = {
  "CE-01": "var(--ce-01-romance)", "CE-02": "var(--ce-02-joy)", "CE-03": "var(--ce-03-sorrow)",
  "CE-04": "var(--ce-04-fury)", "CE-05": "var(--ce-08-valor)", "CE-06": "var(--ce-05-wonder)",
  "CE-07": "var(--ce-06-dread)", "CE-08": "var(--ce-07-revulsion)", "CE-09": "var(--ce-09-serenity)",
};
const CE_NAMES = {
  "CE-01": "Romance", "CE-02": "Joy", "CE-03": "Sorrow", "CE-04": "Fury",
  "CE-05": "Valor", "CE-06": "Dread", "CE-07": "Revulsion", "CE-08": "Wonder", "CE-09": "Serenity",
};

function ApplyModal({ template, onClose }) {
  const [projects, setProjects] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedEpisode, setSelectedEpisode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/projects`, { withCredentials: true }).then(r => setProjects(r.data));
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    axios.get(`${API}/projects/${selectedProject}/episodes`, { withCredentials: true })
      .then(r => setEpisodes(r.data));
  }, [selectedProject]);

  const handleApply = async () => {
    if (!selectedProject || !selectedEpisode) return;
    setLoading(true);
    try {
      await axios.post(`${API}/templates/${template.id}/apply`,
        { project_id: selectedProject, episode_id: selectedEpisode }, { withCredentials: true });
      navigate(`/studio/${selectedProject}`, {
        state: {
          selectedEpisodeId: selectedEpisode,
          openBriefEntry: true,
          templateApplied: template.name,
        },
      });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="relative w-full max-w-sm rounded-2xl p-6 z-10"
        style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}>
        <h3 className="font-bold text-base mb-1">Apply Template</h3>
        <p className="text-[var(--ink-secondary)] text-xs mb-4">"{template.name}" will set episode defaults and send you straight to Scene 1 brief entry</p>
        <div className="space-y-3">
          <div>
            <label className="text-[var(--ink-secondary)] text-xs block mb-1">Select Series</label>
            <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
              <option value="">Choose a series...</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>
          {selectedProject && (
            <div>
              <label className="text-[var(--ink-secondary)] text-xs block mb-1">Select Episode</label>
              <select value={selectedEpisode} onChange={e => setSelectedEpisode(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl text-sm outline-none"
                style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}>
                <option value="">Choose an episode...</option>
                {episodes.map(e => <option key={e.id} value={e.id}>Ep {e.episode_number}: {e.title}</option>)}
              </select>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-[var(--ink-secondary)] text-sm border" style={{ borderColor: "var(--border-subtle)" }}>
              Cancel
            </button>
            <button onClick={handleApply} disabled={!selectedEpisode || loading}
              data-testid="apply-template-button"
              className="flex-1 py-2.5 rounded-xl font-semibold text-sm"
              style={{ background: selectedEpisode ? "var(--accent-gold)" : "var(--surface-card)", cursor: selectedEpisode ? "pointer" : "not-allowed" }}>
              {loading ? "Applying..." : "Apply Template"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGenre, setActiveGenre] = useState("all");
  const [applyTarget, setApplyTarget] = useState(null);

  useEffect(() => {
    axios.get(`${API}/templates`, { withCredentials: true })
      .then(r => setTemplates(r.data))
      .finally(() => setLoading(false));
  }, []);

  const genres = ["all", ...Array.from(new Set(templates.map(t => t.genre || "drama").filter(Boolean)))];

  const filtered = activeGenre === "all"
    ? templates
    : templates.filter(t => (t.genre || "").toLowerCase().includes(activeGenre.toLowerCase()));

  return (
    <div className="min-h-screen" style={{ background: "var(--surface-page)" }}>
      <TopNav />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-black tracking-tight">Template Library</h1>
          <p className="text-[var(--ink-secondary)] text-sm mt-1">Pre-built emotional blueprints for powerful storytelling</p>
        </div>

        {/* Genre filters */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {genres.slice(0, 10).map(g => (
            <button key={g} onClick={() => setActiveGenre(g)}
              data-testid={`genre-filter-${g}`}
              className="px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-all"
              style={activeGenre === g
                ? { background: "var(--accent-gold)", color: "#fff" }
                : { background: "var(--surface-card)", color: "var(--ink-secondary)", border: "1px solid var(--border-subtle)" }}>
              {g === "all" ? "All Templates" : g.replace(/_/g, " ")}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 rounded-xl animate-pulse" style={{ background: "var(--surface-card)" }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen size={48} className="text-[var(--border-subtle)] mx-auto mb-4" />
            <p className="text-[var(--ink-tertiary)]">No templates found for this genre</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((t, i) => {
              const ceColor = CE_COLORS[t.primary_ce] || "var(--accent-gold)";
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="arc-card rounded-xl p-5 flex flex-col"
                  data-testid={`template-card-${t.id}`}
                  style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
                >
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-bold text-sm leading-tight">{t.name}</h3>
                      <EmotionChip ceCode={t.primary_ce} className="ml-2 flex-shrink-0" />
                    </div>
                    <p className="text-[var(--ink-secondary)] text-xs leading-relaxed mb-3 line-clamp-2">{t.description}</p>

                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(t.recommended_ts || []).slice(0, 3).map(ts => (
                        <span key={ts} className="text-xs px-2 py-0.5 rounded-full text-[var(--ink-tertiary)]"
                          style={{ background: "var(--surface-page)", border: "1px solid var(--border-subtle)" }}>{ts}</span>
                      ))}
                      {(t.recommended_ir || []).slice(0, 2).map(ir => (
                        <span key={ir} className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background: `${ceColor}15`, color: ceColor, border: `1px solid ${ceColor}30` }}>{ir}</span>
                      ))}
                    </div>

                    {t.target_platforms && (
                      <div className="text-xs text-[var(--ink-tertiary)]">
                        {(t.target_platforms || []).slice(0, 2).join(", ")}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => setApplyTarget(t)}
                    data-testid={`use-template-${t.id}`}
                    className="mt-4 w-full py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all"
                    style={{ background: ceColor }}
                  >
                    <Zap size={13} />
                    Use Template
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {applyTarget && (
        <ApplyModal template={applyTarget} onClose={() => setApplyTarget(null)} />
      )}
    </div>
  );
}
